import { observable, configure, flow, action } from 'mobx'
import API from '@aws-amplify/api'
import _ from 'lodash'

import { ToastType, ToastTypeEnum } from '../components/Dashboard/Toasts'
import prospects from './prospects'
import related from './related'

configure({ enforceActions: 'always' })

interface Address {
  street?: string
  city?: string
  state?: string
}

interface PhoneNumber {
  category: string
  number: string
}

interface BaseCompany {
  dncCompany: boolean
}

export interface Company extends BaseCompany {
  id: number
  name: string
  address: Address
  website: string
  details?: string
  phoneNumber?: PhoneNumber
  linkedin?: string
  twitter?: string
  facebook?: string
  other?: string
  headCount?: string
  revenueBucket?: string
  industry?: string
  dncCompanies?: string[]
}

interface CompanyInfoError extends BaseCompany {
  results: boolean
}

export type CompanyInfoResult = CompanyInfoError | Company

export class CompanyInfo {
  @observable toasts: ToastType[] = []
  @observable isLoading = false
  @observable company: Company | null = null
  @observable dncChecked = false

  @action
  pushToToasts(toast: ToastType): void {
    const newToast = toast

    if (newToast.show === undefined) {
      newToast.show = true
    }

    if (newToast.delay === undefined) {
      newToast.delay = 3000
    }

    this.setToasts(this.toasts.concat(newToast))
  }

  @action
  popFromToasts(): void {
    const newToasts = _.cloneDeep(this.toasts)

    newToasts.shift()
    this.setToasts(newToasts)
  }

  @action
  setToasts(toasts: ToastType[]): void {
    this.toasts = toasts
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading
  }

  @action
  setCompany(company: Company | null): void {
    this.company = company

    if (company) {
      if (company.dncCompanies) {
        this.setDNCChecked(company.dncCompanies.includes(company.website))
      } else {
        this.setDNCChecked(false)
      }
    }
  }

  @action
  setDNCChecked(dncChecked: boolean): void {
    this.dncChecked = dncChecked
  }

  @action
  getCompany = flow(function*(this: CompanyInfo, website: string) {
    this.setIsLoading(true)
    try {
      const response: CompanyInfoResult = yield API.get(
        'mav-outlook-api',
        `/company/${website}`,
        {},
      )

      // check if company is found
      if (!('results' in response)) {
        this.setCompany(response)
      } else if (response.results === false) {
        this.setCompany(null)
      }

      this.setIsLoading(false)
    } catch (e) {
      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: 'Unable to get company information',
      })

      this.setIsLoading(false)
    }
  })

  @action
  onCompanyDNCChange = flow(function*(this: CompanyInfo, checked: boolean) {
    const initialChecked = this.dncChecked

    this.setDNCChecked(checked)

    const payload = {
      dncCompanies: this.company?.dncCompanies,
    }

    const myInit = { body: payload }

    try {
      let cleanURL = this.company?.website || ''
      cleanURL = cleanURL.replace('https://', '')
      cleanURL = cleanURL.replace('http://', '')
      cleanURL = cleanURL.replace('www.', '')

      yield API.post('mav-outlook-api', `/company/${cleanURL}?dnc=${checked}`, myInit)

      // console.log(response)

      prospects.setCacheValid(false)
      related.setCacheValid(false)
    } catch (e) {
      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: 'Unable to update company DNC setting',
      })

      this.setDNCChecked(initialChecked)
    }
  })

  @action
  clearState(): void {
    this.setToasts([])
    this.setIsLoading(false)
    this.setCompany(null)
    this.setDNCChecked(false)
  }
}

export default new CompanyInfo()
